// Frontend/src/pages/resident/screens/NotificationsPage.tsx
//
// The bell's popover only ever shows the newest 50 in a 24rem scroll box —
// there was nowhere to send someone who wanted to sit with their inbox for a
// minute, or who dismissed the popover before reading something. Same data,
// same routing (routeFor, KIND_ICON/KIND_TINT, useRelativeTime all come from
// lib/notificationDisplay, shared with the bell rather than redefined here),
// full page instead of a corner of the header.
//
// Still capped at 50 — that is the inbox's own limit (api/notifications.ts),
// not something this page adds.

import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import api from '@/api/client';
import type { NotificationItem } from '@/types/notification';
import { Bell, CheckCheck } from "lucide-react";

import { KIND_ICON, KIND_TINT, routeFor, useRelativeTime } from "@/lib/notificationDisplay";
import { useMarkAllRead, useMarkRead, useNotifications } from "@/api/notifications";
import SegmentedTabs from "@/components/common/SegmentedTabs";
import { useLanguage } from "@/contexts/LanguageContext";

type Filter = "all" | "unread";

export default function NotificationsPage({ role = 'resident' }: { role?: 'resident' | 'hoa-admin' | 'super-admin' }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const relativeTime = useRelativeTime();
  const [filter, setFilter] = React.useState<Filter>("all");
  const [params] = useSearchParams();
  const selectedId = params.get('notification');
  // Retrieve a selected older notice directly; the newest-50 list is not a
  // retention rule and must not make a saved emergency link inaccessible.
  const selected = useQuery({
    queryKey: ['notifications', 'detail', selectedId], enabled: !!selectedId,
    queryFn: async () => (await api.get<NotificationItem>(`/notifications/inbox/${encodeURIComponent(selectedId!)}/`)).data,
  });
  React.useEffect(() => {
    if (selectedId && selected.data) document.getElementById(`notification-${selectedId}`)?.scrollIntoView({ block: 'center' });
  }, [selectedId, selected.data]);

  // Always enabled — unlike the popover's useNotifications(open), this page
  // IS the open state.
  const { data: items, isLoading } = useNotifications(true);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const visible = React.useMemo(() => {
    const all = selected.data ? [selected.data, ...(items ?? []).filter(item => item.id !== selected.data.id)] : items ?? [];
    return filter === "unread" ? all.filter((n) => !n.is_read) : all;
  }, [items, filter, selected.data]);

  const unreadCount = (items ?? []).filter((n) => !n.is_read).length;

  function handleClick(item: NotificationItem) {
    if (!item.is_read) markRead.mutate(item.id);
    const route = routeFor(item, role);
    if (route) navigate(route);
  }

  return (
    <div className="relative space-y-4">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      <div className="flex items-start justify-between gap-3 rounded-2xl bg-white/80 p-4 ring-1 ring-black/5 backdrop-blur">
        <div>
          <h2 className="text-[17px] font-semibold text-gray-900">
            {t("notifications.page.title")}
          </h2>
          <p className="mt-0.5 text-[13px] text-gray-600">{t("notifications.page.subtitle")}</p>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[12px] font-semibold text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            {t("notifications.markAllRead")}
          </button>
        )}
      </div>

      <SegmentedTabs<Filter>
        ariaLabel={t("notifications.page.title")}
        value={filter}
        onChange={setFilter}
        items={[
          { key: "all", label: t("notifications.page.all") },
          {
            key: "unread",
            label: t("notifications.page.unreadOnly"),
            count: unreadCount,
          },
        ]}
      />
      {selected.isError && <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-800">{t('notifications.detailUnavailable')}</p>}

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-white/70 ring-1 ring-black/5" />
          ))}
        </div>
      )}

      {!isLoading && visible.length === 0 && (
        <div className="rounded-2xl bg-white/80 py-14 text-center ring-1 ring-black/5">
          <Bell className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("notifications.empty")}</p>
        </div>
      )}

      {visible.length > 0 && (
        <ul className="space-y-2">
          {visible.map((item) => {
            const Icon = KIND_ICON[item.kind] ?? Bell;
            const tint = KIND_TINT[item.kind] ?? "#64748B";
            return (
              <li key={item.id} id={`notification-${item.id}`} className={item.id === selectedId ? 'rounded-2xl ring-2 ring-blue-600' : ''}>
                <button
                  type="button"
                  onClick={() => handleClick(item)}
                  className={`flex w-full items-start gap-3 rounded-2xl p-3.5 text-left ring-1 transition hover:bg-gray-50 ${
                    item.is_read ? "bg-white/80 ring-black/5" : "bg-blue-50/60 ring-blue-100"
                  }`}
                >
                  <span
                    className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                    style={{ background: `${tint}14`, color: tint }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-start gap-2">
                      <span
                        className={`min-w-0 flex-1 text-[14px] ${
                          item.is_read ? "font-medium text-gray-700" : "font-semibold text-gray-900"
                        }`}
                      >
                        {item.title}
                      </span>
                      {!item.is_read && (
                        <span
                          className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: "#DC2626" }}
                          aria-hidden
                        />
                      )}
                    </span>
                    {item.body && (
                      <span className="mt-1 block whitespace-pre-wrap break-words text-[13px] text-gray-500">{item.body}</span>
                    )}
                    <span className="mt-1.5 block text-[11px] text-gray-400">
                      {relativeTime(item.created_at)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
